import { defineStore } from "pinia";
import { jwtDecode } from "jwt-decode";
import { clearStorage } from "@/utils/storage";
import { resetRouter } from "@/router";
import { resetRoutesAdded } from "@/permission";
import {
  isAuthenticated as keycloakAuthenticated,
  logout as keycloakLogout,
} from "@/services/keycloak";
import { CURRENT_ACCOUNT_SIGNATURE_ATTR } from "@/utils/account-manager";

interface AuthState {
  authenticated: boolean;
  user_id: string | null;
}

/**
 * The acting users row: `sub` of the chosen account's signature. A person has one users row per
 * account (#261), so this changes with the account switcher; the Keycloak token only names the
 * person (identity).
 */
function userIdOfStoredSignature(): string | null {
  try {
    const signature = localStorage.getItem(CURRENT_ACCOUNT_SIGNATURE_ATTR);
    return signature ? (jwtDecode<{ sub?: string }>(signature).sub ?? null) : null;
  } catch {
    return null;
  }
}

export const useAuthStore = defineStore("auth", {
  state: (): AuthState => ({
    authenticated: keycloakAuthenticated(),
    user_id: keycloakAuthenticated() ? userIdOfStoredSignature() : null,
  }),

  getters: {
    isAuthenticated: (state): boolean => state.authenticated,
  },

  actions: {
    /** Called by setCurrentAccount with the `sub` of the new account signature. */
    setUserId(user_id: string | null) {
      this.user_id = user_id;
    },

    // `navigate: false` only clears the local session (tests, callers that route on their own).
    // Otherwise the Keycloak session ends too and the browser lands on `path` — by default the
    // app root, which leads to the login page again.
    async logout({
      navigate = true,
      path = "/",
    }: { navigate?: boolean; path?: string } = {}) {
      clearStorage();

      // Reset other stores
      const { useAccountStore } = await import("./account");
      const { usePermissionStore } = await import("./permission");
      const { useMyUserStore } = await import("./myuser");
      const { useUsersStore } = await import("./users");
      const { useStaticContentStore } = await import("./staticContent");
      const { resetSessionStores } = await import("./session");
      const { forgetSessionIdentity } = await import("@/utils/account-manager");

      useAccountStore().resetState();
      usePermissionStore().resetState();
      useMyUserStore().resetState();
      useUsersStore().resetState();
      useStaticContentStore().resetState();
      // Mail state, spaces, week, timer, list filters, contact import, account logo.
      resetSessionStores();
      forgetSessionIdentity();

      // Reset router and routes added flag
      resetRouter();
      resetRoutesAdded();

      this.user_id = null;

      if (navigate) {
        await keycloakLogout(path);
      }
    },

    resetState() {
      this.$reset();
    },
  },
});
